import { Fragment, useState, useEffect, useCallback } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, MagnifyingGlassIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import { employeeAPI } from '../services/api';
import toast from 'react-hot-toast';

const ImpersonationModal = ({ isOpen, onClose }) => {
  const { user, startImpersonation } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [impersonating, setImpersonating] = useState(false);

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const response = await employeeAPI.getAll({ limit: 500, active_only: true });
      // Filter out the current admin user
      const filteredEmployees = (response.data.employees || response.data || [])
        .filter(emp => emp.employee_ID !== user?.employee_ID);
      setEmployees(filteredEmployees);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
      toast.error("Couldn't load employees. Check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }, [user?.employee_ID]);

  useEffect(() => {
    if (isOpen) {
      fetchEmployees();
    }
  }, [isOpen, fetchEmployees]);

  const handleImpersonate = async (employee) => {
    setImpersonating(true);
    try {
      const result = await startImpersonation(employee.employee_ID);
      if (result.success) {
        toast.success(`Now viewing as ${employee.first_name} ${employee.last_name}`);
        onClose();
      } else {
        toast.error(result.error || 'Failed to start impersonation');
      }
    } catch (error) {
      toast.error('Failed to start impersonation');
    } finally {
      setImpersonating(false);
    }
  };

  const filteredEmployees = employees.filter(emp => {
    const searchLower = searchTerm.toLowerCase();
    const fullName = `${emp.first_name} ${emp.last_name}`.toLowerCase();
    const username = (emp.username || '').toLowerCase();
    const email = (emp.email || '').toLowerCase();
    const department = (emp.department_name || '').toLowerCase();
    const role = (emp.role_name || emp.role || 'user').toLowerCase();

    return fullName.includes(searchLower) ||
           username.includes(searchLower) ||
           email.includes(searchLower) ||
           department.includes(searchLower) ||
           role.includes(searchLower);
  });

  const getRoleBadgeColor = (role) => {
    const roleLower = (role || 'user').toLowerCase();
    switch (roleLower) {
      case 'admin':
        return 'bg-red-100 text-red-700';
      case 'coordinator':
        return 'bg-blue-100 text-blue-700';
      case 'travel lead':
        return 'bg-purple-100 text-purple-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-soft-xl transition sm:my-8 sm:w-full sm:max-w-2xl">
                <div className="bg-white">
                  {/* Header */}
                  <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                    <div>
                      <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                        Impersonate User
                      </Dialog.Title>
                      <p className="mt-1 text-sm text-gray-500">
                        Select a user to view the application as them
                      </p>
                    </div>
                    <button
                      type="button"
                      className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                      onClick={onClose}
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="size-6" aria-hidden="true" />
                    </button>
                  </div>

                  {/* Search */}
                  <div className="border-b border-gray-200 px-6 py-4">
                    <div className="relative">
                      <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search by name, username, email, department, or role…"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="block w-full rounded-md border-0 py-2 pl-10 pr-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                      />
                    </div>
                  </div>

                  {/* Employee List */}
                  <div className="max-h-96 overflow-y-auto">
                    {loading ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="size-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
                      </div>
                    ) : filteredEmployees.length === 0 ? (
                      <div className="py-12 text-center">
                        <UserCircleIcon className="mx-auto size-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
                        <p className="mt-1 text-sm text-gray-500">
                          {searchTerm ? 'Try adjusting your search' : 'No employees available'}
                        </p>
                      </div>
                    ) : (
                      <ul className="divide-y divide-gray-200">
                        {filteredEmployees.map((employee) => (
                          <li key={employee.employee_ID}>
                            <button
                              type="button"
                              onClick={() => handleImpersonate(employee)}
                              disabled={impersonating}
                              className="flex w-full items-center gap-x-4 px-6 py-4 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              <div
                                className="size-10 flex-shrink-0 rounded-full flex items-center justify-center text-white font-medium"
                                style={{ backgroundColor: employee.profile_color || '#005581' }}
                              >
                                {employee.first_name?.[0]}{employee.last_name?.[0]}
                              </div>
                              <div className="flex-1 min-w-0 text-left">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {employee.first_name} {employee.last_name}
                                </p>
                                <p className="text-sm text-gray-500 truncate">
                                  {employee.email || employee.username}
                                </p>
                              </div>
                              <div className="flex flex-col items-end gap-1">
                                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getRoleBadgeColor(employee.role_name || employee.role)}`}>
                                  {employee.role_name || employee.role || 'user'}
                                </span>
                                {employee.department_name && (
                                  <span className="text-xs text-gray-500">
                                    {employee.department_name}
                                  </span>
                                )}
                              </div>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="border-t border-gray-200 bg-gray-50 px-6 py-3">
                    <p className="text-xs text-gray-500">
                      <strong>Note:</strong> While impersonating, you will see the application exactly as this user sees it.
                      All actions will be logged.
                    </p>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ImpersonationModal;
